import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/schema";
import RecordForm, { buildInitialValues, type FormValues } from "../components/RecordForm";
import type { DetailResponse } from "../lib/types";

export default function ResourceNew() {
  const { key } = useParams<{ key: string }>();
  const { resource, isLoading, features } = useResource(key);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<DetailResponse>(`/superadmin/resources/${key}`, { values }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["resource", key] });
      queryClient.invalidateQueries({ queryKey: ["activity"] });
      navigate(`/r/${key}/${data.row.id}`);
    },
    // Model validations are the real gate — surface their wording rather than a
    // generic "something went wrong".
    onError: (err) => setError(err instanceof ApiError ? err.message : "Kunde inte skapa posten"),
  });

  if (isLoading) return <div className="panel h-96 animate-pulse" />;
  if (!resource) return <div className="panel p-6 text-sm text-ink-400">Okänd resurs: {key}</div>;
  if (resource.creatable.length === 0) {
    return <div className="panel p-6 text-sm text-ink-400">Den här resursen kan inte skapas här.</div>;
  }

  return (
    <div className="max-w-2xl space-y-4">
      <Link to={`/r/${key}`} className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-300">
        <ArrowLeft className="h-3 w-3" /> {resource.label}
      </Link>

      <div>
        <h1 className="text-xl font-semibold text-white">{resource.new_label || `Ny ${resource.label}`}</h1>
        <p className="text-sm text-ink-500">Fält markerade med * krävs.</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="panel p-4">
        <RecordForm
          fields={resource.creatable}
          columns={resource.detail_columns}
          required={resource.required}
          initial={buildInitialValues(resource.creatable, resource.detail_columns)}
          geocoding={Boolean(features?.geocoding)}
          submitLabel="Skapa"
          busy={create.isPending}
          onSubmit={(values) => { setError(null); create.mutate(values); }}
          onCancel={() => navigate(`/r/${key}`)}
        />
      </div>
    </div>
  );
}
